import { removeUndefinedAndNullValues } from '@/lib/utils/object';
import { AllowedAny, AnyObject } from '../types/common';
import { titleizeWithSpaces } from '../utils/titleize';

const logLevels = ['debug', 'info', 'warn', 'error'] as const;
type LogLevel = (typeof logLevels)[number];

type Logger = Record<LogLevel, (...params: AllowedAny[]) => void>;

class LoggerService {
  private readonly logger: Record<LogLevel, (...params: AllowedAny[]) => void>;

  private userId: string | null = null;
  private sessionId: string | null = null;

  constructor() {
    this.logger = logLevels.reduce((acc: Logger, level) => {
      acc[level] = (log: AllowedAny) => {
        return console[level]({ ...log, meta: this.getMetadata() });
      };
      return acc;
    }, {} as Logger);
  }

  public info(message: string, context?: AnyObject) {
    this.logger.info(message, context);
  }

  public warn(message: string, context?: AnyObject) {
    this.logger.warn(message, context);
  }

  public error(message: string, error?: unknown, context?: AnyObject) {
    this.logger.error(message, context || error ? { ...context, error } : undefined);
  }

  public duration(message: string, startTime: number, context?: AnyObject) {
    const duration = Math.round(performance.now() - startTime);
    this.logger.info(message, { ...context, durationMetric: true, duration });
  }

  public setUserId(userId: string | null) {
    this.userId = userId;
  }

  private generatePrefix(log: AnyObject) {
    let prefix = '';

    if (log.context) prefix += `[${this.humanizeContext(log.context)}]`;
    if (log.duration) prefix += `[${log.duration}ms]`;
    if (prefix.length > 0) prefix += ' ';

    return prefix;
  }

  private humanizeContext(context: unknown) {
    const string = String(context);
    return humanizeContextMap[string] ?? titleizeWithSpaces(string);
  }

  private getMetadata() {
    return removeUndefinedAndNullValues({
      userId: this.userId,
      sessionId: this.sessionId,
      version: 1,
    });
  }
}

export const Logger = new LoggerService();

const humanizeContextMap: Record<string, string> = {
  api: 'API',
};
